import { z } from "zod";
import { DEFAULT_SELECTABLE_COLOR, SELECTABLE_COLOR_OPTIONS } from "@/lib/colors/selectable-colors";

export const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
export const DEFAULT_APPOINTMENT_TYPE_COLOR = DEFAULT_SELECTABLE_COLOR;
export const DEFAULT_APPOINTMENT_TYPE_TEXT_COLOR = "#2B2B2B";
export const INVERTED_APPOINTMENT_TYPE_TEXT_COLOR = "#FFFFFF";
// External (source-managed) appointments use a flat neutral fill instead of a type color, so they
// read as one read-only group. Theme tokens keep them correct in light and dark.
export const EXTERNAL_APPOINTMENT_BACKGROUND_COLOR = "var(--color-neutral-4)";
export const EXTERNAL_APPOINTMENT_TEXT_COLOR = "var(--color-neutral-9)";

export const AppointmentTypeColorSchema = z
  .string()
  .regex(HEX_COLOR_REGEX, "Color must be a valid hex color code (e.g., #FF5733)");

export const APPOINTMENT_TYPE_COLOR_OPTIONS = SELECTABLE_COLOR_OPTIONS;

/**
 * Returns true when the input is a valid 6-digit hex color.
 */
export function isHexColor({ color }: { color?: string | null }): boolean {
  return typeof color === "string" && HEX_COLOR_REGEX.test(color);
}

/**
 * Normalizes a persisted color to uppercase hex or returns undefined when invalid.
 */
export function normalizeAppointmentTypeColor({
  color,
}: {
  color?: string | null;
}): string | undefined {
  if (typeof color !== "string" || !isHexColor({ color })) {
    return undefined;
  }

  return color.toUpperCase();
}

/**
 * Resolves the background color for appointment cells, preserving the historical yellow fallback.
 */
export function getAppointmentTypeBackgroundColor({ color }: { color?: string | null }): string {
  return normalizeAppointmentTypeColor({ color }) ?? DEFAULT_APPOINTMENT_TYPE_COLOR;
}

function toSrgbChannel({ value }: { value: string }): number {
  return Number.parseInt(value, 16) / 255;
}

function toLinearChannel({ value }: { value: number }): number {
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function getRelativeLuminance({ color }: { color: string }): number {
  const red = toLinearChannel({ value: toSrgbChannel({ value: color.slice(1, 3) }) });
  const green = toLinearChannel({ value: toSrgbChannel({ value: color.slice(3, 5) }) });
  const blue = toLinearChannel({ value: toSrgbChannel({ value: color.slice(5, 7) }) });

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function getContrastRatio({
  backgroundColor,
  textColor,
}: {
  backgroundColor: string;
  textColor: string;
}): number {
  const backgroundLuminance = getRelativeLuminance({ color: backgroundColor });
  const textLuminance = getRelativeLuminance({ color: textColor });
  const lighter = Math.max(backgroundLuminance, textLuminance);
  const darker = Math.min(backgroundLuminance, textLuminance);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Computes a readable text color for an appointment background.
 */
export function getAppointmentTypeTextColor({ color }: { color?: string | null }): string {
  const backgroundColor = getAppointmentTypeBackgroundColor({ color });
  const defaultContrastRatio = getContrastRatio({
    backgroundColor,
    textColor: DEFAULT_APPOINTMENT_TYPE_TEXT_COLOR,
  });
  const invertedContrastRatio = getContrastRatio({
    backgroundColor,
    textColor: INVERTED_APPOINTMENT_TYPE_TEXT_COLOR,
  });

  return defaultContrastRatio >= invertedContrastRatio
    ? DEFAULT_APPOINTMENT_TYPE_TEXT_COLOR
    : INVERTED_APPOINTMENT_TYPE_TEXT_COLOR;
}
